import React, { useMemo } from "react";

// Builds a compact page list: first + last always shown, a window around the
// current page, and "…" ellipsis markers for gaps (e.g. 1 … 4 5 6 … 20).
function buildPaginationItems(currentPage, totalPages) {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  const delta = 1;
  const left = Math.max(2, currentPage - delta);
  const right = Math.min(totalPages - 1, currentPage + delta);
  const items = [1];
  if (left > 2) items.push("left-ellipsis");
  for (let i = left; i <= right; i++) items.push(i);
  if (right < totalPages - 1) items.push("right-ellipsis");
  items.push(totalPages);
  return items;
}

/**
 * Shared pagination control (first/previous, numbered pages with ellipsis
 * windowing, next/last, and a "current / total" label). Originally built for
 * the Attendance page; reused across tables that page through large lists.
 */
export default function Pagination({
  currentPage,
  totalPages,
  onPageChange,
  theme = "light",
  disabled = false,
  isRTL = false,
  bleed = false,
  showCount = true,
  style,
}) {
  const paginationItems = useMemo(
    () => buildPaginationItems(currentPage, totalPages),
    [currentPage, totalPages]
  );

  if (!totalPages || totalPages <= 1) return null;

  const go = (p) => onPageChange(Math.min(Math.max(1, p), totalPages));

  const navBtnStyle = (btnDisabled) => ({
    minWidth: "34px",
    height: "34px",
    padding: "0 10px",
    borderRadius: "8px",
    border: `1px solid ${theme === "dark" ? "#1e293b" : "#e2e8f0"}`,
    background: theme === "dark" ? "#1e293b" : "#ffffff",
    color: btnDisabled
      ? theme === "dark" ? "#475569" : "#cbd5e1"
      : theme === "dark" ? "#f1f5f9" : "#1e293b",
    cursor: btnDisabled || disabled ? "not-allowed" : "pointer",
    opacity: btnDisabled ? 0.6 : 1,
    fontSize: "14px",
    fontWeight: "600",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "all 0.2s ease",
  });

  return (
    <div
      className="nx-pagination"
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        gap: "6px",
        padding: "16px",
        flexWrap: "wrap",
        borderTop: `1px solid ${theme === "dark" ? "#1e293b" : "#e2e8f0"}`,
        background: theme === "dark" ? "#0f172a" : "#f8fafc",
        direction: isRTL ? "rtl" : "ltr",
        ...(bleed
          ? {
              margin: "16px -30px -30px -30px",
              borderBottomLeftRadius: "1rem",
              borderBottomRightRadius: "1rem",
            }
          : {}),
        ...style,
      }}
    >
      <button className="nx-page-first" disabled={disabled || currentPage === 1} onClick={() => go(1)} style={navBtnStyle(currentPage === 1)} title="First" aria-label="First page">«</button>
      <button disabled={disabled || currentPage === 1} onClick={() => go(currentPage - 1)} style={navBtnStyle(currentPage === 1)} title="Previous" aria-label="Previous page">‹</button>

      {paginationItems.map((item) => {
        if (item === "left-ellipsis" || item === "right-ellipsis") {
          return (
            <span
              key={item}
              style={{
                minWidth: "24px",
                textAlign: "center",
                color: theme === "dark" ? "#64748b" : "#94a3b8",
                fontSize: "13px",
                userSelect: "none",
              }}
            >
              …
            </span>
          );
        }
        const active = currentPage === item;
        return (
          <button
            key={item}
            disabled={disabled}
            onClick={() => go(item)}
            style={{
              minWidth: "34px",
              height: "34px",
              padding: "0 10px",
              borderRadius: "8px",
              border: active
                ? `1px solid ${theme === "dark" ? "#3b82f6" : "#2563eb"}`
                : `1px solid ${theme === "dark" ? "#1e293b" : "#e2e8f0"}`,
              background: active
                ? theme === "dark" ? "#3b82f6" : "#2563eb"
                : theme === "dark" ? "#1e293b" : "#ffffff",
              color: active ? "#fff" : theme === "dark" ? "#f1f5f9" : "#1e293b",
              cursor: disabled ? "wait" : "pointer",
              transition: "all 0.2s ease",
              fontSize: "13px",
              fontWeight: active ? "700" : "500",
            }}
          >
            {item}
          </button>
        );
      })}

      <button disabled={disabled || currentPage === totalPages} onClick={() => go(currentPage + 1)} style={navBtnStyle(currentPage === totalPages)} title="Next" aria-label="Next page">›</button>
      <button className="nx-page-last" disabled={disabled || currentPage === totalPages} onClick={() => go(totalPages)} style={navBtnStyle(currentPage === totalPages)} title="Last" aria-label="Last page">»</button>

      {showCount && (
        <span
          className="nx-page-count"
          style={{
            marginLeft: "10px",
            fontSize: "13px",
            color: theme === "dark" ? "rgba(255,255,255,0.6)" : "#6b7280",
            whiteSpace: "nowrap",
          }}
        >
          {currentPage} / {totalPages}
        </span>
      )}
    </div>
  );
}
